import React, { useEffect, useState } from "react";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import usePairsStore from "../stores/courses/pair";

const PairsManagement = () => {
  const {
    pairs,
    filters,
    pagination,
    isLoading,
    isSubmitting,
    getPairs,
    toggleRecommend,
    setFilters,
    setPage,
    exchanges,
    isExchangeLoading,
    getExchanges,
  } = usePairsStore();

  const [localFilters, setLocalFilters] = useState(filters);

  // ✅ Debounce typing
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(localFilters);
    }, 500);
    return () => clearTimeout(timeout);
  }, [localFilters]);

  useEffect(() => {
    getExchanges();
  }, []);

  // ✅ Fetch data when filters or page changes
  useEffect(() => {
    getPairs(null, { page: pagination.page });
  }, [filters, pagination.page]);

  const totalPages = Math.ceil(pagination.count / pagination.pageSize);

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <h1 className="text-2xl font-semibold text-gray-800">
          Pairs Management
        </h1>

        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={localFilters.exchange_id}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                exchange_id: Number(e.target.value),
              })
            }
            disabled={isExchangeLoading}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm pr-10 appearance-none"
          >
            {isExchangeLoading ? (
              <option>Loading exchanges...</option>
            ) : exchanges.length > 0 ? (
              exchanges.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.exchange.toUpperCase()}
                </option>
              ))
            ) : (
              <option>No exchanges</option>
            )}
          </select>

          <input
            type="text"
            placeholder="Target Currency (e.g. BTC)"
            value={localFilters.target_currency}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                target_currency: e.target.value.toUpperCase(),
              })
            }
            className="border border-gray-300 rounded-md px-3 py-2 text-sm w-36"
          />

          <input
            type="text"
            placeholder="Base Currency (e.g. USDT)"
            value={localFilters.base_currency}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                base_currency: e.target.value.toUpperCase(),
              })
            }
            className="border border-gray-300 rounded-md px-3 py-2 text-sm w-36"
          />

          <button
            onClick={() => getPairs(null, 1)}
            disabled={isLoading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center"
          >
            <ArrowPathIcon
              className={`h-4 w-4 mr-1 ${
                isLoading ? "animate-spin" : "opacity-80"
              }`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <ArrowPathIcon className="h-8 w-8 text-indigo-500 animate-spin" />
        </div>
      ) : pairs.length === 0 ? (
        <p className="text-gray-500 text-center py-6">No pairs found.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    "#",
                    "Coin",
                    "Base Currency",
                    "Exchange",
                    "Decimals",
                    "CLS Decimals",
                    "Buy Min Qty",
                    "Sell Min Qty",
                    "Min Order Qty",
                    "Base Order Qty",
                    "Recommended",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pairs.map((pair, idx) => (
                  <tr key={pair.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {(pagination.page - 1) * pagination.pageSize + idx + 1}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {pair.coin}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {pair.base_currency}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">
                      {pair.exchange}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {pair.decimals}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {pair.cls_decimals}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {pair.buy_min_qty}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {pair.sell_min_qty}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {pair.min_order_qty}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {pair.base_order_qty}
                    </td>
                    <td className="px-4 py-3">
                      {pair.is_recommended ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-3 w-3 mr-1" /> Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <XCircleIcon className="h-3 w-3 mr-1" /> No
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        disabled={isSubmitting}
                        onClick={() => toggleRecommend(pair.id)}
                        className={`${
                          pair.is_recommended
                            ? "bg-yellow-500 hover:bg-yellow-600"
                            : "bg-green-600 hover:bg-green-700"
                        } text-white px-3 py-1 rounded-md text-xs`}
                      >
                        {pair.is_recommended ? "Unrecommend" : "Recommend"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ✅ Pagination Footer */}
          {pagination.count > pagination.pageSize && (
            <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
              <p>
                Showing{" "}
                <span className="font-semibold">
                  {(pagination.page - 1) * pagination.pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold">
                  {Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.count
                  )}
                </span>{" "}
                of <span className="font-semibold">{pagination.count}</span>{" "}
                results
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, pagination.page - 1))}
                  disabled={!pagination.previous}
                  className="flex items-center px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronLeftIcon className="h-4 w-4 mr-1" /> Prev
                </button>
                <span>
                  Page {pagination.page} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setPage(Math.min(totalPages, pagination.page + 1))
                  }
                  disabled={!pagination.next}
                  className="flex items-center px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
                >
                  Next <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PairsManagement;
